import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  getListOrders,
  getListOrganizers,
} from "../../redux/actions/organizerActions";
import DefaultLayout from "../../layouts/DefaultLayout";
import Loader from "../../components/Loader";
import Alert from "../../components/Alert";
import { baseURLFile } from "../../constants";
import { deleteOrder, getListOrder } from "../../redux/actions/orderActions";
import Paginate from "../../components/Paginate";
import ConfirmationModal from "../../components/ConfirmationModal";

function OrderScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const page = searchParams.get("page") || "1";

  const [eventType, setEventType] = useState("");
  const [orderId, setOrderId] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loadEvent, setLoadEvent] = useState(false);

  const [status, setStatus] = useState("All");
  const [search, setSearch] = useState("");
  const [organizerSelect, setOrganizerSelect] = useState("");

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const listOrders = useSelector((state) => state.getListOrder);
  const { orders, loadingListOrders, errorListOrders, pages } = listOrders;

  const listOrganizers = useSelector((state) => state.getListOrganizerReducer);
  const { organizers, loadingListOrganizers, errorListOrganizers } =
    listOrganizers;

  const orderDelete = useSelector((state) => state.deleteOrder);
  const { successOrderDelete, loadingOrderDelete, errorOrderDelete } =
    orderDelete;

  const redirect = "/";

  useEffect(() => {
    if (!userInfo) {
      navigate(redirect);
    } else {
      dispatch(getListOrganizers("0"));
      dispatch(getListOrder(page, organizerSelect, status, search));
    }
  }, [navigate, userInfo, dispatch, page]);

  useEffect(() => {
    if (successOrderDelete) {
      dispatch(getListOrganizers("0"));
      dispatch(getListOrder("1", organizerSelect, status, search));
    }
  }, [successOrderDelete]);

  return (
    <DefaultLayout>
      <div className="animate-fadeIn">
        {/* Modern Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <a
                href="/dashboard"
                className="inline-flex items-center text-sm font-medium text-primary hover:text-secondary transition-colors duration-200"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  ></path>
                </svg>
                Dashboard
              </a>
            </li>
            <li aria-current="page">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-secondary"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <span className="ml-1 text-sm font-medium text-secondary md:ml-2">
                  Orders
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Header with background */}
        <div
          className="relative mb-8 rounded-xl overflow-hidden bg-gradient-to-r from-primary to-secondary shadow-lg"
          style={{
            backgroundImage: "url('/assets/background-auth-screen.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-secondary/80"></div>
          <div className="relative z-10 px-6 py-8 md:px-10 md:py-12">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Order Management
                </h1>
                <p className="text-white/80 max-w-xl">
                  View and manage all customer orders. Track status, view details, and process orders efficiently.
                </p>
              </div>
              <div className="mt-4 md:mt-0 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                <div className="flex items-center">
                  <div className="mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-white">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white/70 text-xs">Total Orders</p>
                    <p className="text-white font-bold text-xl">{orders?.length || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Filter Section */}

        {/*  */}
        {/* Orders Table */}
        <div className="rounded-xl border border-stroke bg-whiter px-6 pt-6 pb-5 shadow-sm hover:shadow-md transition-all duration-300 mb-8">
          <div className="container mx-auto flex flex-col">
            <h2 className="text-xl font-semibold text-black mb-4 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2 text-primary">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
              </svg>
              Orders List
            </h2>

            {loadingListOrders ? (
              <div className="flex justify-center items-center h-80">
                <Loader />
              </div>
            ) : errorListOrders ? (
              <div className="flex justify-center items-center h-80">
                <Alert type={"error"} message={errorListOrders} />
              </div>
            ) : orders?.length === 0 ? (
              <div className="flex flex-col justify-center items-center h-60 bg-gray-1/30 rounded-lg border border-stroke/50">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12 text-bodydark mb-3">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                </svg>
                <p className="text-bodydark text-lg font-medium">No orders found</p>
                <p className="text-bodydark/70 text-sm mt-1">Try adjusting your filters to see more results</p>
              </div>
            ) : (
              <div className="w-full overflow-x-auto">
                <table className="w-full table-auto">
                  <thead>
                    <tr className="bg-gray-2 text-left">
                      <th className="min-w-[80px] py-4 px-4 font-semibold text-black text-sm rounded-l-lg">
                        Order ID
                      </th>
                      <th className="min-w-[180px] py-4 px-4 font-semibold text-black text-sm">
                        Client
                      </th>
                      <th className="min-w-[120px] py-4 px-4 font-semibold text-black text-sm">
                        Total
                      </th>
                      <th className="min-w-[100px] py-4 px-4 font-semibold text-black text-sm">
                        Tip
                      </th>
                      <th className="min-w-[180px] py-4 px-4 font-semibold text-black text-sm">
                        Date
                      </th>
                      <th className="min-w-[120px] py-4 px-4 font-semibold text-black text-sm">
                        Status
                      </th>
                      <th className="min-w-[120px] py-4 px-4 font-semibold text-black text-sm rounded-r-lg text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders?.map((item, index) => (
                      <tr
                        key={index}
                        className={`hover:bg-gray-1 transition-colors duration-200 group ${
                          index === orders.length - 1 ? "" : "border-b border-stroke"
                        }`}
                      >
                        <td className="py-4 px-4">
                          <span className="text-sm font-medium text-black bg-gray-2 py-1 px-2.5 rounded-md">
                            #{item.id}
                          </span>
                        </td>

                        <td className="py-4 px-4">
                          <h5 className="text-sm font-medium text-black">
                            {item.client?.full_name ?? "---"}
                          </h5>
                        </td>

                        <td className="py-4 px-4">
                          <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                            ${parseFloat(item.total_order).toFixed(2) ?? "0.00"}
                          </div>
                        </td>

                        <td className="py-4 px-4">
                          {item.tip_amount > 0 ? (
                            <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-600 border border-green-200">
                              ${parseFloat(item.tip_amount).toFixed(2)}
                            </div>
                          ) : (
                            <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                              No Tip
                            </div>
                          )}
                        </td>

                        <td className="py-4 px-4">
                          <p className="text-sm text-body">
                            {item.created_at ?? "---"}
                          </p>
                        </td>

                        <td className="py-4 px-4">
                          {item.order_status ? (
                            <div className="flex items-center">
                              <span className={`w-2.5 h-2.5 rounded-full mr-2 ${
                                item.order_status === "Completed"
                                  ? "bg-success"
                                  : item.order_status === "Pending"
                                    ? "bg-warning"
                                    : item.order_status === "Cancelled"
                                      ? "bg-danger"
                                      : "bg-blue-500"
                              }`}></span>
                              <span className="text-sm font-medium text-black">
                                {item.order_status}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-bodydark">---</span>
                          )}
                        </td>

                        <td className="py-4 px-4">
                          <div className="flex items-center justify-center space-x-2">
                            <Link
                              to={"/orders/detail/" + item.id}
                              className="hover:text-primary text-body p-1.5 rounded-full hover:bg-primary/10 transition-colors duration-200"
                              title="View Order Details"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                              </svg>
                            </Link>

                            <button
                              onClick={() => {
                                setEventType("delete");
                                setOrderId(item.id);
                                setIsOpen(true);
                              }}
                              className="hover:text-danger text-body p-1.5 rounded-full hover:bg-danger/10 transition-colors duration-200"
                              title="Delete Order"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="mt-6">
                  <Paginate
                    route={"/orders?"}
                    search={""}
                    page={page}
                    pages={pages}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        {/*  */}
        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={isOpen}
          message={
            eventType === "delete"
              ? "Are you sure you want to delete this order? This action cannot be undone."
              : "Are you sure you want to proceed with this action?"
          }
          onConfirm={async () => {
            if (eventType === "cancel") {
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
              setOrderId("");
            } else if (eventType === "delete" && orderId !== "") {
              setLoadEvent(true);
              dispatch(deleteOrder(orderId));
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
            } else {
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
              setOrderId("");
            }
          }}
          onCancel={() => {
            setIsOpen(false);
            setEventType("");
            setLoadEvent(false);
            setOrderId("");
          }}
          loadEvent={loadEvent}
        />
      </div>
    </DefaultLayout>
  );
}

export default OrderScreen;
